/**
 * ADM-07 课程商品列表页（course-product-catalog:SC-01~SC-25 前端）。
 *
 * 筛选区：名称模糊 + 省/市级联 + 包含 SKU 下拉 + 状态（含已下架）+ 查询/重置。
 * 列表：封面缩略（未上传占位色块）、商品名称（点击打开只读详情抽屉）、归属省/市、卖点前 2、
 *       SKU 打包摘要、SKU 数、总课时、原价（划线）、商品定价、首付/尾款、订单引用数、状态、更新时间。
 * 行操作：编辑（仅草稿/已下架开放）、复制（调 create 建 DRAFT 副本加「（副本）」后缀）、
 *         上架/下架（二次确认）、删除（二次确认；服务端守卫 409 时 toast）。
 * 仿 CourseSkusPage：直接 antd Table 自管 data/page/loading（design D12）。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Input, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import type { ColumnsType } from "antd/es/table";
import {
  productApi,
  type ProductItem,
  type ProductListQuery,
} from "../../api/products.api";
import { courseSkuApi, type CourseSkuItem } from "../../api/course-skus.api";
import { ApiError } from "../../api/transport";
import { shelfStatusLabel, toSelectOptions } from "../../api/labels";
import { EllipsisLink } from "../../components/EllipsisCell";
import { MoneyDisplay } from "../../components/MoneyDisplay";
import { useDraggableModal } from "../../components/useDraggableModal";
import { ProductEditModal } from "./ProductEditModal";
import { ProductDetailDrawer } from "./ProductDetailDrawer";
import { REGION_TREE } from "./ProductEditModal";
import { HasPermission, useHasPermission } from "../../auth/HasPermission";

const { Title } = Typography;

/** 状态标签颜色映射（商品三值）。 */
const shelfStatusColor: Record<string, string> = {
  DRAFT: "default",
  ON_SHELF: "green",
  OFF_SHELF: "orange",
};

/** 提取错误信息文案。 */
function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "操作失败";
}

export function ProductsPage() {
  const modal = useDraggableModal();
  const [data, setData] = useState<ProductItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<ProductListQuery>({});
  const [skus, setSkus] = useState<CourseSkuItem[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProductItem | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  const [viewing, setViewing] = useState<ProductItem | null>(null);

  // SKU 下拉与名称映射用：加载非 DISABLED SKU（listSkusForSelector 经 /course-skus 拉取，一期数据量小）
  useEffect(() => {
    courseSkuApi
      .listCourseSkus({ pageSize: 100 })
      .then((res) => setSkus(res.items.filter((s) => s.status !== "DISABLED")))
      .catch(() => undefined);
  }, []);

  const skuOptions = useMemo(
    () => skus.map((s) => ({ value: s.id, label: s.name })),
    [skus],
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await productApi.listProducts({
        ...filters,
        page,
        pageSize,
      });
      setData(result.items);
      setTotal(result.total);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const openCreate = () => {
    setEditing(null);
    setDuplicating(false);
    setModalOpen(true);
  };
  const openEdit = (record: ProductItem) => {
    setEditing(record);
    setDuplicating(false);
    setModalOpen(true);
  };
  const openDuplicate = (record: ProductItem) => {
    setEditing(record);
    setDuplicating(true);
    setModalOpen(true);
  };

  /** 上架（编辑弹窗保存后调用）。 */
  const handlePublish = async (id: string) => {
    try {
      await productApi.publishProduct(id);
      message.success("已上架");
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
      throw err;
    }
  };

  /** 行内下架（二次确认）。 */
  const handleUnpublish = async (id: string) => {
    try {
      await productApi.unpublishProduct(id);
      message.success("已下架");
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  /** 删除（二次确认；服务端守卫：上架中/被订单引用 409 toast）。 */
  const handleRemove = async (record: ProductItem) => {
    try {
      await productApi.removeProduct(record.id);
      message.success("已删除");
      void fetchList();
    } catch (err) {
      // 按错误码稳定分支（后端文案调整不影响此提示）
      if (
        err instanceof ApiError &&
        err.code === "product-catalog.product_referenced_by_orders"
      ) {
        modal.warning({
          title: "无法删除",
          content:
            "该商品已被订单引用，只能下架不可删除（历史订单以快照为准）。",
        });
        return;
      }
      message.error(errorMessage(err));
    }
  };

  // 行操作统一 EDIT 闸控（对齐后端 @Roles）；无权限整列不渲染，越权由后端 403 兜底
  const canManage = useHasPermission("ADM-07", "EDIT");
  const { columns, components } = useResizableColumns<ProductItem>([
    {
      title: "封面",
      dataIndex: "coverImageUrl",
      width: 76,
      render: (url: string | null) =>
        url ? (
          <img
            src={url}
            alt="封面"
            style={{
              width: 48,
              height: 32,
              objectFit: "cover",
              borderRadius: 4,
            }}
          />
        ) : (
          <div
            style={{
              width: 48,
              height: 32,
              background: "#e3f2ef",
              borderRadius: 4,
            }}
          />
        ),
    },
    {
      title: "商品名称",
      dataIndex: "name",
      width: 180,
      ellipsis: true,
      render: (name: string, record) => (
        <EllipsisLink value={name} onClick={() => setViewing(record)} />
      ),
    },
    {
      title: "归属省/市",
      dataIndex: "provinceName",
      width: 130,
      render: (v: string, r) => `${v} · ${r.cityName}`,
    },
    {
      title: "卖点标签",
      dataIndex: "features",
      width: 140,
      render: (features: string[]) =>
        features.slice(0, 2).map((f) => <Tag key={f}>{f}</Tag>),
    },
    {
      title: "SKU打包摘要",
      dataIndex: "skuConfigs",
      width: 200,
      render: (configs: ProductItem["skuConfigs"]) => (
        <div>
          {configs.map((c) => (
            // 逐项形态（period-pass-admin-ui:SC-04）：历史数据缺省 chargeMode 按课时包渲染不报错
            <div key={c.id} style={{ fontSize: 12 }}>
              {c.skuName ?? c.skuId.slice(0, 6)} ·{' '}
              {c.chargeMode === 'PERIOD'
                ? `期限${c.periodDays ?? '-'}天（畅学）`
                : `×${c.lessonCount ?? '-'}课时`}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "SKU数量",
      dataIndex: "skuConfigs",
      width: 70,
      align: "center",
      render: (v: ProductItem["skuConfigs"]) => v.length,
    },
    {
      title: "课时/期限",
      dataIndex: "skuConfigs",
      width: 130,
      align: "center",
      render: (v: ProductItem["skuConfigs"]) => {
        const lessons = v
          .filter((c) => c.chargeMode !== 'PERIOD')
          .reduce((sum, c) => sum + (c.lessonCount ?? 0), 0);
        const passes = v.filter((c) => c.chargeMode === 'PERIOD').length;
        if (passes === 0) return `${lessons} 课时`;
        return lessons > 0 ? `${lessons} 课时 + ${passes} 张期限卡` : `${passes} 张期限卡`;
      },
    },
    {
      title: "原价",
      dataIndex: "originalPrice",
      width: 100,
      render: (v: number) => (
        <Text delete type="secondary">
          <MoneyDisplay amountInCents={v} />
        </Text>
      ),
    },
    {
      title: "商品价",
      dataIndex: "price",
      width: 100,
      render: (v: number) => <MoneyDisplay amountInCents={v} />,
    },
    {
      title: "首付/尾款",
      dataIndex: "depositRatio",
      width: 110,
      render: (v: number, r) =>
        `${(v * 100).toFixed(0)}% / ${r.finalDueDays}天`,
    },
    {
      title: "订单引用数",
      dataIndex: "orderRefCount",
      width: 90,
      align: "center",
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (status: string) => (
        <Tag color={shelfStatusColor[status]}>
          {(shelfStatusLabel as Record<string, string>)[status] ?? status}
        </Tag>
      ),
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString("zh-CN") : "-"),
    },
    ...(canManage
      ? ([
          {
            title: "操作",
            key: "action",
            width: 240,
            fixed: "right",
            render: (_, record) => {
              const canEdit =
                record.status === "DRAFT" || record.status === "OFF_SHELF";
              return (
                <Space size="small">
                  {canEdit ? (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => openEdit(record)}
                    >
                      编辑
                    </Button>
                  ) : (
                    <Tooltip title="已上架商品不可编辑，请先下架">
                      <Button type="link" size="small" disabled>
                        编辑
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    type="link"
                    size="small"
                    onClick={() => openDuplicate(record)}
                  >
                    复制
                  </Button>
                  {canEdit && (
                    <Popconfirm
                      title="确认上架该商品？"
                      description="上架后家长端该地区可见并可报名"
                      onConfirm={() => handlePublish(record.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button type="link" size="small">
                        上架
                      </Button>
                    </Popconfirm>
                  )}
                  {record.status === "ON_SHELF" && (
                    <Popconfirm
                      title="确认下架该商品？"
                      description="下架后家长端不可见、不可新报名"
                      onConfirm={() => handleUnpublish(record.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button type="link" size="small">
                        下架
                      </Button>
                    </Popconfirm>
                  )}
                  {canEdit && (
                    <Popconfirm
                      title={
                        record.orderRefCount > 0
                          ? "商品已被订单引用，是否改为下架？"
                          : "确认删除该商品？"
                      }
                      onConfirm={() =>
                        record.orderRefCount > 0
                          ? handleUnpublish(record.id)
                          : handleRemove(record)
                      }
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button type="link" size="small" danger>
                        删除
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              );
            },
          },
        ] as ColumnsType<ProductItem>)
      : []),
  ]);

  return (
    <div>
      <Title level={4}>课程商品</Title>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="商品名称"
            allowClear
            value={filters.name ?? ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, name: e.target.value || undefined }))
            }
            style={{ width: 200 }}
          />
          <Select
            placeholder="省份"
            allowClear
            value={filters.provinceId}
            onChange={(val) =>
              setFilters((f) => ({
                ...f,
                provinceId: val ?? undefined,
                cityId: undefined,
              }))
            }
            style={{ width: 120 }}
            options={REGION_TREE.map((p) => ({ label: p.name, value: p.code }))}
          />
          <Select
            placeholder="城市"
            allowClear
            value={filters.cityId}
            onChange={(val) =>
              setFilters((f) => ({ ...f, cityId: val ?? undefined }))
            }
            style={{ width: 120 }}
            disabled={!filters.provinceId}
            options={(
              REGION_TREE.find((p) => p.code === filters.provinceId)?.cities ??
              []
            ).map((c) => ({
              label: c.name,
              value: c.code,
            }))}
          />
          <Select
            placeholder="包含SKU"
            allowClear
            value={filters.skuId}
            onChange={(val) =>
              setFilters((f) => ({ ...f, skuId: val ?? undefined }))
            }
            style={{ width: 160 }}
            options={skuOptions}
            showSearch
            optionFilterProp="label"
          />
          <Select
            placeholder="状态"
            allowClear
            value={filters.status}
            onChange={(val) =>
              setFilters((f) => ({ ...f, status: val ?? undefined }))
            }
            style={{ width: 110 }}
            options={toSelectOptions(shelfStatusLabel).filter(
              (o) => o.value !== "DISABLED",
            )}
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => void fetchList()}
          >
            查询
          </Button>
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card>
        <div style={{ marginBottom: 16 }}>
          {/* 无 ADM-07 CREATE 权限不渲染入口，越权直调由后端 403 兜底 */}
          <HasPermission menuKey="ADM-07" action="CREATE">
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              新增
            </Button>
          </HasPermission>
        </div>
        <Table<ProductItem>
          scroll={{ x: "max-content" }}
          rowKey="id"
          columns={columns}
          components={components}
          dataSource={data}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>
      <ProductEditModal
        open={modalOpen}
        editing={editing}
        duplicate={duplicating}
        skuOptions={skus}
        onSaved={() => void fetchList()}
        onPublish={handlePublish}
        onClose={() => setModalOpen(false)}
      />
      <ProductDetailDrawer product={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

const { Text } = Typography;
